/**
 * Custom reactive Directionality service for dynamic RTL/LTR switching.
 *
 * Angular CDK's built-in Directionality reads `dir` from the document root
 * once at construction and never updates. This causes overlays (dialogs,
 * dropdowns, menus) to remain stuck in their initial direction because they
 * are rendered outside the component tree's `Dir` directive scope.
 *
 * This service extends CDK Directionality and overrides the `value` getter
 * to read direction reactively from SettingsService.appDirection().
 * It also emits `change` events so subscribers (including overlay positioning)
 * can react to direction changes.
 *
 * Provided at the app root level in app.config.ts to replace the default
 * CDK Directionality singleton.
 */

import { Injectable, inject, effect } from '@angular/core';
import { Directionality, Direction } from '@angular/cdk/bidi';
import { SettingsService } from './settings.service';

@Injectable()
export class AppDirectionality extends Directionality {
  private ss = inject(SettingsService);
  private _direction: Direction = 'ltr';

  constructor() {
    super(document);

    // Initialize with current direction
    this._direction = (this.ss.appDirection() || 'ltr') as Direction;

    // React to direction changes and emit events for CDK consumers
    effect(() => {
      const dir = this.ss.appDirection() as Direction;
      if (this._direction !== dir) {
        this._direction = dir;
        this.change.emit(dir);
      }
    });
  }

  override get value(): Direction {
    return this._direction;
  }
}
